import { useEffect, useState } from "react";
import { Outlet, Navigate, useLocation } from "react-router";
import {
  useAuthSession,
  useAuthUser,
} from "@/components/providers/auth-context";
import { useProvisioning } from "@/lib/hooks/use-provisioning";
import { useIsNewAccount } from "@/lib/hooks/use-is-new-account";
import { useSignOut } from "@/lib/hooks/use-sign-out";
import {
  AccountGreeting,
  OnboardingHeader,
  ProgressIndicator,
} from "@/components/onboarding";
import { FullScreenLoader } from "@/components/ui/loader";
import { useOnboardingStore } from "@/lib/stores/onboarding-store";
import { ONBOARDING_STEPS } from "./steps";

export function OnboardingLayout() {
  const { isAuthenticated, isSessionLoading } = useAuthSession();
  const { isUserLoading } = useAuthUser();
  const { isProvisioned, isProvisioning, provisioningError, profile } =
    useProvisioning();
  const { completedSteps, completeStep, createdSiteId } = useOnboardingStore();
  const { pathname } = useLocation();
  const isNewAccount = useIsNewAccount(profile);
  const signOut = useSignOut();

  // A tenant that has had a site has onboarded (task 495), and no step here
  // is for it any more: a bookmark or a stale tab must not re-run the
  // organization and plan steps. Decided once, from the profile the layout
  // first has. Adding the first site, one step from the end, is what sets
  // `firstSiteCreatedAt`, and a refetch of the profile while someone reads
  // the setup step after it (the client refetches on window focus) must not
  // pull them out of it. The site and setup steps for the site this browser
  // added (`createdSiteId`) stay open even so, to a reload or Back between
  // them: they show that site, not steps the tenant has to take again.
  const [onboardedOnEntry, setOnboardedOnEntry] = useState<boolean | null>(
    null,
  );
  if (profile && onboardedOnEntry === null) {
    setOnboardedOnEntry(Boolean(profile.tenant.firstSiteCreatedAt));
  }

  // Sync completed steps from backend state
  // This handles the case where localStorage is cleared but backend has state
  useEffect(() => {
    if (!profile?.tenant) return;

    // If org setup is complete on backend but not in local state, sync it
    if (profile.tenant.isOrgSetupComplete && !completedSteps.includes("org")) {
      completeStep("org");
    }
  }, [profile, completedSteps, completeStep]);

  if (isSessionLoading || isUserLoading) {
    return <FullScreenLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isProvisioning) {
    return <FullScreenLoader />;
  }

  if (provisioningError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <div className="text-destructive">Failed to set up your account</div>
        <div className="text-sm text-muted-foreground">
          {provisioningError.message}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="text-sm text-primary underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!isProvisioned) {
    return <FullScreenLoader />;
  }

  const onAddedSiteStep =
    createdSiteId !== null &&
    (pathname === "/onboarding/site" || pathname === "/onboarding/setup");
  if (onboardedOnEntry && !onAddedSiteStep) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      <OnboardingHeader />
      <main className="container mx-auto max-w-2xl px-4 py-8">
        {profile && (
          <div className="mb-6">
            <AccountGreeting
              isNewAccount={isNewAccount}
              email={profile.user.email}
              onSignOut={() => void signOut()}
            />
          </div>
        )}
        <ProgressIndicator steps={ONBOARDING_STEPS} />
        <div className="mt-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
